import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import TemplateDetailsPage from "./pages/TemplateDetailsPage";
import DesktopDetailsPage from "./pages/DesktopDetailsPage";
import AccountPage from "./pages/AccountPage";
import SettingsPage from "./pages/SettingsPage";
import PrivateRoute from "./components/PrivateRoute";
import { initializeKeycloak, refreshToken } from "./slices/keycloakSlice";
import { login } from "./slices/userSlice";
import NotAllowedPage from "./pages/NotAllowedPage";
import InfoPage from "./pages/InfoPage";
import { environment } from "./config.js";
import LandingPage from "./pages/LandingPage";
import CreateDesktopPage from "./pages/CreateDesktopPage";
import GlobalSSEListener from "./components/GlobalSSEListener";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isInitializing, setIsInitializing] = useState(true);
  const isAuthenticated = useSelector((state) => state.user?.authenticated);
  const isEnvNoAuth = environment === "production-no-auth";

  useEffect(() => {
    if (!isEnvNoAuth) {
      dispatch(initializeKeycloak(navigate)).finally(() => {
        setIsInitializing(false);
      });
    } else {
      dispatch(login());
      setIsInitializing(false);
    }
  }, [dispatch, navigate, isEnvNoAuth]);

  useEffect(() => {
    let interval;
    if (isAuthenticated) {
      interval = setInterval(() => {
        dispatch(refreshToken());
      }, 10000); // 10000 ms => 10 s
    }
    return () => clearInterval(interval);
  }, [dispatch, isAuthenticated]);

  if (isInitializing) {
    return <div>Loading...</div>;
  }

  if (isEnvNoAuth) {
    return (
      <div className="main-container">
        <GlobalSSEListener />
        <ToastContainer position="bottom-right" autoClose={3000} />

        <Routes>
          <Route path="/" element={<HomePage envIsNoAuth={isEnvNoAuth} />} />
          <Route
            path="/create-desktop"
            element={<CreateDesktopPage envIsNoAuth={isEnvNoAuth} />}
          />
          <Route
            path="/account"
            element={<AccountPage envIsNoAuth={isEnvNoAuth} />}
          />
          <Route
            path="/settings"
            element={<SettingsPage envIsNoAuth={isEnvNoAuth} />}
          />
          <Route path="/templates/:id" element={<TemplateDetailsPage />} />
          <Route path="/desktops/:id" element={<DesktopDetailsPage />} />
          <Route
            path="/not-allowed"
            element={<NotAllowedPage envIsNoAuth={isEnvNoAuth} />}
          />
          <Route path="/info" element={<InfoPage />} />
        </Routes>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="main-container">
        <Routes>
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="main-container">
      <GlobalSSEListener />
      <ToastContainer position="bottom-right" autoClose={3000} />

      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <HomePage envIsNoAuth={isEnvNoAuth} />
            </PrivateRoute>
          }
        />
        <Route
          path="/create-desktop"
          element={
            <PrivateRoute>
              <CreateDesktopPage envIsNoAuth={isEnvNoAuth} />
            </PrivateRoute>
          }
        />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <AccountPage envIsNoAuth={isEnvNoAuth} />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <SettingsPage envIsNoAuth={isEnvNoAuth} />
            </PrivateRoute>
          }
        />
        <Route
          path="/templates/:id"
          element={
            <PrivateRoute>
              <TemplateDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/desktops/:id"
          element={
            <PrivateRoute>
              <DesktopDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/not-allowed"
          element={<NotAllowedPage envIsNoAuth={isEnvNoAuth} />}
        />
        <Route path="/info" element={<InfoPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default App;
